import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useWarehouseLocationsSWR } from 'components/routes/Pages/Refund/hooks';
import React, { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useRegistration from '../../hooks/authentication/useRegistration';
import {
    PartnerRegistrationRequest,
    RegistrationComponentsProps,
} from '../../models/Authentication/Registration';
import {
    B2B_URL,
    LEGAL_SITE_URLS,
    PATTERNS,
} from '../../utils/constans/constans';
import SnackbarUtils from '../../utils/SnackbarUtils';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import SelectStyled from '../shared/SelectStyled';
import TextFieldStyled from '../shared/TextFieldStyled';
import { AuthPageTitle } from '../shared/Typography/AuthenticationsPageTitle';
import { SubmitButtonWrapper } from '../styled/Form/SubmitButtonWrapper';

export const PartnerRegistrationForm: React.FC<RegistrationComponentsProps> = (
    props
) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<PartnerRegistrationRequest>();
    const { handleStateChange } = props;
    const termAndConditions = B2B_URL + LEGAL_SITE_URLS.TERMS_AND_CONDITIONS;
    const privacyStatement = B2B_URL + LEGAL_SITE_URLS.PRIVACY_STATEMENT;

    const registration = useRegistration();
    const { t } = useTranslation();
    //handle default stock change
    const [defaultStock, setDefaultStock] = useState('');
    const [showTankYou, setShowThankYou] = useState(false);

    const handleChange = (event: SelectChangeEvent) => {
        setDefaultStock(event.target.value as string);
    };

    const { warehouseLocations, loading: loadingWarehouseLocations } =
        useWarehouseLocationsSWR();

    const warehouseLocationOptions = useMemo(
        () =>
            warehouseLocations?.map(
                (warehouseLocation) => warehouseLocation.bc_location_name
            ),
        [warehouseLocations]
    );

    const [isLoading, setIsLoading] = useState(false);

    const onSubmit: SubmitHandler<PartnerRegistrationRequest> = (data) => {
        // setShowThankYou(true);
        setIsLoading(true);
        registration
            .sendPartnerRegistration(data)
            .then(() => {
                SnackbarUtils.success(
                    t('NOTIFICATION.REGISTRATION_SUCCESS').toString()
                );
                setShowThankYou(true);
                // navigate('/login', { replace: true });
            })
            .catch(() => {
                setIsLoading(false);
            });
    };

    if (loadingWarehouseLocations) return null;

    return (
        <>
            <AuthPageTitle>
                <Box sx={{ textAlign: 'center' }}>
                    {t('REGISTRATION_FORM_FIELDS.BC_USER_REG_TITLE').toString()}
                    <br />
                    {t(
                        'REGISTRATION_FORM_FIELDS.BC_USER_REG_TITLE_SUB'
                    ).toString()}
                </Box>
            </AuthPageTitle>
            {showTankYou && (
                <Box>{t('REGISTRATION_FORM_FIELDS.THANK_YOU')}</Box>
            )}
            {!showTankYou && (
                <>
                    <Typography paragraph>
                        {t('REGISTRATION_FORM_FIELDS.HAVE_ACCOUNT').toString()}{' '}
                        <Link to="/login">
                            {' '}
                            {t(
                                'REGISTRATION_FORM_FIELDS.HAVE_ACCOUNT_LINK'
                            ).toString()}
                        </Link>
                    </Typography>
                    <Typography paragraph>
                        {t(
                            'REGISTRATION_FORM_FIELDS.NO_ACCOUNT_IN_BC'
                        ).toString()}{' '}
                        <Box
                            component={'span'}
                            sx={{ color: '#007cb0', cursor: 'pointer' }}
                            onClick={() => handleStateChange('new')}
                        >
                            {' '}
                            {t(
                                'REGISTRATION_FORM_FIELDS.NO_ACCOUNT_IN_BC_LINK'
                            ).toString()}
                        </Box>
                    </Typography>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Email address */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'REGISTRATION_FORM_FIELDS.EMAIL'
                                ).toString()}
                                error={!!errors.email}
                                {...register('email', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                    pattern: {
                                        value: PATTERNS.VALID_EMAIL,
                                        message: t(
                                            'FORM_VALIDATION_ERROR.EMAIL'
                                        ).toString(),
                                    },
                                })}
                                helperText={
                                    errors.email ? errors.email.message : ' '
                                }
                            />
                        </FormControl>
                        {/* Company */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'REGISTRATION_FORM_FIELDS.COMPANY'
                                ).toString()}
                                error={!!errors.company}
                                {...register('company', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                })}
                                helperText={
                                    errors.company
                                        ? errors.company.message
                                        : ' '
                                }
                            />
                        </FormControl>
                        {/* Phone */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'REGISTRATION_FORM_FIELDS.PHONE'
                                ).toString()}
                                error={!!errors.phone}
                                {...register('phone', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                    pattern: {
                                        value: PATTERNS.PHONE_NUMBER,
                                        message: t(
                                            'FORM_VALIDATION_ERROR.PHONE'
                                        ).toString(),
                                    },
                                })}
                                helperText={
                                    errors.phone ? errors.phone.message : ' '
                                }
                            />
                        </FormControl>
                        {/* Default stock */}
                        <FormControl fullWidth variant="filled">
                            <InputLabel
                                sx={{
                                    color: '#999999',
                                }}
                            >
                                {t(
                                    'REGISTRATION_FORM_FIELDS.DEFAULT_STORE'
                                ).toString()}
                            </InputLabel>
                            <SelectStyled
                                label={t(
                                    'REGISTRATION_FORM_FIELDS.DEFAULT_STORE'
                                ).toString()}
                                {...register('store', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                    onChange: (e) => handleChange(e),
                                })}
                                value={defaultStock}
                            >
                                {warehouseLocationOptions?.map(
                                    (stock, index) => (
                                        <MenuItem key={index} value={stock}>
                                            {stock}
                                        </MenuItem>
                                    )
                                )}
                            </SelectStyled>
                            <FormHelperText error={!!errors.store}>
                                &#8203;
                                {errors.store && errors.store.message}
                            </FormHelperText>
                        </FormControl>
                        {/* Note */}
                        <Typography paragraph>
                            {t('REGISTRATION_FORM_FIELDS.NOTE').toString()}
                        </Typography>
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'REGISTRATION_FORM_FIELDS.NOTE_DESC'
                                ).toString()}
                                multiline
                                rows={4}
                                {...register('note')}
                            />
                        </FormControl>
                        {/* Terms of service */}
                        <FormControl fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...register('privacy_notice', {
                                            required: t(
                                                'FORM_VALIDATION_ERROR.REQUIRED'
                                            ).toString(),
                                        })}
                                    />
                                }
                                label={
                                    <>
                                        <p>
                                            {t(
                                                'REGISTRATION_FORM_FIELDS.TERMS_OF_SERVICE'
                                            ).toString()}
                                            <a
                                                href={termAndConditions}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {t(
                                                    'REGISTRATION_FORM_FIELDS.TERMS_OF_SERVICE_LINK'
                                                ).toString()}
                                            </a>
                                            {t(
                                                'REGISTRATION_FORM_FIELDS.AND'
                                            ).toString()}
                                            <a
                                                href={privacyStatement}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {t(
                                                    'REGISTRATION_FORM_FIELDS.ASZF'
                                                ).toString()}
                                            </a>
                                        </p>
                                    </>
                                }
                            />
                            <FormHelperText error={!!errors.privacy_notice}>
                                {errors.privacy_notice &&
                                    errors.privacy_notice.message}
                            </FormHelperText>
                        </FormControl>
                        {/* Submit */}
                        <SubmitButtonWrapper>
                            <PrimaryBtn type="submit" disabled={isLoading}>
                                {t(
                                    'REGISTRATION_FORM_FIELDS.SEND_EXIST_FORM'
                                ).toString()}
                            </PrimaryBtn>
                        </SubmitButtonWrapper>
                    </form>
                </>
            )}
        </>
    );
};
